/**
 * OnboardingWizard Component
 * Guides new users through setting up their first dashboard
 */

import { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import {
  LayoutGrid,
  Briefcase,
  FolderKanban,
  User,
  DollarSign,
  BarChart3,
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
} from 'lucide-react';
import { DASHBOARD_TEMPLATES, createLayoutFromTemplate } from './templates';

// Icon mapping for templates
const TEMPLATE_ICONS = {
  layout: LayoutGrid,
  briefcase: Briefcase,
  folder: FolderKanban,
  user: User,
  dollar: DollarSign,
  chart: BarChart3,
};

// Color mapping for templates
const TEMPLATE_COLORS = {
  slate: 'bg-slate-500/10 text-slate-500',
  blue: 'bg-blue-500/10 text-blue-500',
  green: 'bg-green-500/10 text-green-500',
  purple: 'bg-purple-500/10 text-purple-500',
  emerald: 'bg-emerald-500/10 text-emerald-500',
  orange: 'bg-orange-500/10 text-orange-500',
};

export const OnboardingWizard = ({
  open,
  onOpenChange,
  onComplete,
  userName,
}) => {
  const [step, setStep] = useState(1);
  const [dashboardName, setDashboardName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const totalSteps = 3;

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = () => {
    const layout = selectedTemplate ? createLayoutFromTemplate(selectedTemplate) : [];
    onComplete({
      name: dashboardName || `${userName}'s Dashboard`,
      template: selectedTemplate,
      layout,
    });
    onOpenChange(false);
    // Reset state
    setStep(1);
    setDashboardName('');
    setSelectedTemplate(null);
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return true; // Welcome step, always can proceed
      case 2:
        return true; // Dashboard name is optional
      case 3:
        return true; // Template selection is optional (can choose blank)
      default:
        return true;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
        {/* Progress indicator */}
        <div className="bg-muted/30 px-6 py-3 border-b border-border/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Step {step} of {totalSteps}</span>
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "h-1.5 w-8 rounded-full transition-colors",
                    i < step ? "bg-primary" : "bg-muted"
                  )}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="p-6">
            <DialogHeader className="text-center mb-6">
              <div className="mx-auto mb-4 p-4 rounded-2xl bg-primary/10 w-fit">
                <Sparkles className="w-10 h-10 text-primary" />
              </div>
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Welcome to Executive Dashboards!
              </DialogTitle>
              <DialogDescription className="text-base mt-2">
                Hi {userName}! Let's set up your personalized dashboard in just a few steps.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/30">
                <div className="p-2 rounded-lg bg-primary/10">
                  <LayoutGrid className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Drag & Drop Widgets</h4>
                  <p className="text-sm text-muted-foreground">Build your dashboard with customizable widgets</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/30">
                <div className="p-2 rounded-lg bg-primary/10">
                  <BarChart3 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Track What Matters</h4>
                  <p className="text-sm text-muted-foreground">Add metrics, KPIs, checklists, and more</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/30">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Briefcase className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Start from Templates</h4>
                  <p className="text-sm text-muted-foreground">Choose a pre-built layout or start from scratch</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Name your dashboard */}
        {step === 2 && (
          <div className="p-6">
            <DialogHeader className="text-center mb-6">
              <DialogTitle className="text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Name Your Dashboard
              </DialogTitle>
              <DialogDescription>
                Give your dashboard a name (you can change this later)
              </DialogDescription>
            </DialogHeader>

            <div className="max-w-sm mx-auto">
              <Input
                placeholder={`${userName}'s Dashboard`}
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                className="text-center text-lg h-12"
                autoFocus
              />
              <p className="text-xs text-muted-foreground text-center mt-2">
                Leave blank to use the default name
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Choose a template */}
        {step === 3 && (
          <div className="p-6">
            <DialogHeader className="text-center mb-4">
              <DialogTitle className="text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Choose a Template
              </DialogTitle>
              <DialogDescription>
                Start with a pre-built layout or create your own
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3 max-h-[320px] overflow-y-auto pr-1">
              {DASHBOARD_TEMPLATES.map((template) => {
                const Icon = TEMPLATE_ICONS[template.icon] || LayoutGrid;
                const isSelected = selectedTemplate === template.id;
                
                return (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={cn(
                      "p-4 rounded-lg border text-left transition-all",
                      isSelected
                        ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                        : "border-border/50 hover:border-border hover:bg-muted/30"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "p-2 rounded-lg",
                        TEMPLATE_COLORS[template.color] || TEMPLATE_COLORS.slate
                      )}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-sm truncate">{template.name}</h4>
                          {isSelected && (
                            <Check className="w-4 h-4 text-primary flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {template.description}
                        </p>
                        {template.layout.length > 0 && (
                          <Badge variant="secondary" className="mt-2 text-xs">
                            {template.layout.length} widgets
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer with navigation */}
        <div className="flex items-center justify-between p-6 pt-0">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={step === 1}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>

          {step < totalSteps ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="gap-2"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              className="gap-2"
            >
              <Check className="w-4 h-4" />
              Create Dashboard
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
